import React, { useMemo, useState } from "react";

import Drawer from "./Drawer";
import {
  draftTargets,
  newTargetRecord,
  REVIEW_META,
  stageTarget,
  stageTargetRemoval,
  SURFACE_LABEL,
  TARGET_ID_PATTERN,
  validTargetPath,
} from "./targets";
import {
  type SignalAdminDraft,
  type SignalAdminRegistry,
  type TargetPatch,
  type TargetRecord,
  type TargetSurface,
} from "./types";
import Button from "../../Components/Button";

interface TargetRegistryPanelProps {
  registry: SignalAdminRegistry;
  draft: SignalAdminDraft;
  /** The applied config's target changes, to mark staged rows. */
  appliedTargets: Record<string, TargetPatch>;
  onDraftChange: (next: SignalAdminDraft) => void;
  onOpenMappings: (targetId: string) => void;
}

type Panel =
  | { kind: "inspect"; id: string }
  | { kind: "edit"; id: string | null }
  | { kind: "availability"; id: string; mode: "disable" | "reject" };

const REVIEW_ORDER: Record<TargetRecord["review_status"], number> = {
  needs_review: 0,
  confirmed: 1,
  rejected: 2,
};

const labelClass =
  "text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500";

const inputClass = (invalid = false) =>
  `w-full rounded-lg border px-3 py-2 text-[13px] text-gray-900 focus:outline-none focus:ring-2 ${
    invalid
      ? "border-red-400 focus:ring-red-200"
      : "border-gray-300 focus:border-brand focus:ring-brand/20"
  }`;

const Pill: React.FC<{ className: string; children: React.ReactNode }> = ({
  className,
  children,
}) => (
  <span
    className={`inline-flex rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ring-1 ring-inset ${className}`}
  >
    {children}
  </span>
);

const routeText = (t: TargetRecord) =>
  t.route ||
  (t.kind === "assess"
    ? "Opens the AL assessment in place"
    : t.kind === "brief"
      ? "Opens the Pre-call Brief in place"
      : "No route");

interface EditForm {
  id: string;
  label: string;
  surface_type: TargetSurface;
  parent_page: string;
  route: string;
  description: string;
  personas: string;
  permission: string;
  required_context: string;
  enabled: boolean;
}

const TargetEditor: React.FC<{
  target: TargetRecord;
  isNew: boolean;
  existingIds: Set<string>;
  onSave: (id: string, patch: TargetPatch) => void;
  onClose: () => void;
  /** Admin-authored targets only; blocked while anything still uses it. */
  onRemove?: () => void;
}> = ({ target, isNew, existingIds, onSave, onClose, onRemove }) => {
  const [form, setForm] = useState<EditForm>({
    id: target.id,
    label: target.label,
    surface_type: target.surface_type,
    parent_page: target.parent_page,
    route: target.route,
    description: target.description,
    personas: target.personas.join(", "),
    permission: target.permission,
    required_context: target.required_context,
    enabled: target.review_status === "rejected" ? true : target.enabled,
  });
  const [touched, setTouched] = useState(false);
  const set = <K extends keyof EditForm>(key: K, value: EditForm[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const errors: Partial<Record<keyof EditForm, string>> = {};
  const id = form.id.trim().toLowerCase().replace(/\s+/g, "_");
  if (isNew && !TARGET_ID_PATTERN.test(id))
    errors.id =
      "Use 2–64 lowercase letters, numbers or underscores, starting with a letter.";
  else if (isNew && existingIds.has(id))
    errors.id = "That target ID already exists.";
  if (form.label.trim().length < 3) errors.label = "A target name is required.";
  if (form.surface_type === "unclassified")
    errors.surface_type = "Classify it as a Page or Tab before confirming it.";
  if (form.surface_type === "tab" && !form.parent_page.trim())
    errors.parent_page = "A tab needs a parent page.";
  if (!target.seeded && !validTargetPath(form.route.trim()))
    errors.route =
      "Use an in-app path starting with “/”; {account_id} is the only placeholder.";
  if (target.seeded && !target.kind)
    errors.route =
      "This reference has no route ExecBridge can open, so it can't be confirmed. Reject it instead.";
  if (!form.permission.trim())
    errors.permission = "A permission boundary is required.";
  const valid = Object.keys(errors).length === 0;

  const save = () => {
    setTouched(true);
    if (!valid) return;
    onSave(id, {
      label: form.label.trim(),
      surface_type: form.surface_type,
      parent_page: form.surface_type === "tab" ? form.parent_page.trim() : "",
      ...(target.seeded ? {} : { route: form.route.trim() }),
      description: form.description.trim(),
      personas: form.personas
        .split(",")
        .map(p => p.trim())
        .filter(Boolean),
      permission: form.permission.trim(),
      required_context: form.required_context.trim(),
      review_status: "confirmed",
      enabled: form.enabled,
    });
  };

  const field = (
    key: keyof EditForm,
    label: string,
    input: React.ReactNode,
    hint?: React.ReactNode
  ) => (
    <label className="flex flex-col gap-1">
      <span className={labelClass}>{label}</span>
      {input}
      {hint && <span className="text-[11.5px] text-gray-500">{hint}</span>}
      {touched && errors[key] && (
        <span className="text-[11.5px] text-red-600">{errors[key]}</span>
      )}
    </label>
  );

  const needsReview = target.review_status === "needs_review";
  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow={
        <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
          {isNew ? "New target" : needsReview ? "Target review" : "Edit target"}
        </span>
      }
      title={isNew ? "Add ExecBridge target" : target.label}
      subtitle={isNew ? undefined : target.id}
    >
      <div className="mb-5 rounded-lg border border-brand/20 bg-brand-faint px-3 py-2.5 text-[12px] text-gray-700">
        <strong className="text-gray-900">
          {needsReview
            ? "Imported legacy reference"
            : "Admin-authored registry record"}
        </strong>{" "}
        {needsReview
          ? "Check that this is a real ExecBridge page or tab and fix its details before confirming it."
          : "Saving confirms this page or tab as a legitimate target."}
      </div>
      <div className="flex flex-col gap-3.5">
        {isNew &&
          field(
            "id",
            "Stable target ID",
            <input
              value={form.id}
              onChange={e => set("id", e.target.value)}
              placeholder="e.g. account_actions"
              className={inputClass(touched && !!errors.id)}
            />
          )}
        {field(
          "label",
          "Target name",
          <input
            value={form.label}
            onChange={e => set("label", e.target.value)}
            placeholder="User-facing page or tab name"
            className={inputClass(touched && !!errors.label)}
          />
        )}
        {field(
          "surface_type",
          "Surface type",
          <select
            value={form.surface_type}
            onChange={e => set("surface_type", e.target.value as TargetSurface)}
            className={inputClass(touched && !!errors.surface_type)}
          >
            <option value="page">Page</option>
            <option value="tab">Tab</option>
            <option value="unclassified">Unclassified legacy reference</option>
          </select>
        )}
        {form.surface_type === "tab" &&
          field(
            "parent_page",
            "Parent page",
            <input
              value={form.parent_page}
              onChange={e => set("parent_page", e.target.value)}
              placeholder="e.g. My Accounts"
              className={inputClass(touched && !!errors.parent_page)}
            />
          )}
        {target.seeded
          ? field(
              "route",
              "Route",
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-[12px] text-gray-700">
                {routeText(target)}
              </div>,
              <>
                ExecBridge routes this target itself, so its route can&apos;t be
                edited.
                {target.legacy_route && (
                  <>
                    {" "}
                    Legacy reference:{" "}
                    <span className="font-mono">{target.legacy_route}</span>
                  </>
                )}
              </>
            )
          : field(
              "route",
              "Route",
              <input
                value={form.route}
                onChange={e => set("route", e.target.value)}
                placeholder="/account-synopsis/{account_id}"
                className={`${inputClass(touched && !!errors.route)} font-mono`}
              />,
              "In-app path; {account_id} is filled in from the card."
            )}
        {field(
          "description",
          "Description",
          <textarea
            value={form.description}
            onChange={e => set("description", e.target.value)}
            rows={2}
            className={inputClass()}
          />
        )}
        {field(
          "personas",
          "Supported personas",
          <input
            value={form.personas}
            onChange={e => set("personas", e.target.value)}
            placeholder="AE, CSM, Sponsor"
            className={inputClass()}
          />
        )}
        {field(
          "permission",
          "Permission boundary",
          <input
            value={form.permission}
            onChange={e => set("permission", e.target.value)}
            placeholder="e.g. Assigned account team"
            className={inputClass(touched && !!errors.permission)}
          />
        )}
        {field(
          "required_context",
          "Required route context",
          <input
            value={form.required_context}
            onChange={e => set("required_context", e.target.value)}
            placeholder="account_id"
            className={inputClass()}
          />
        )}
        {field(
          "enabled",
          "Organization state",
          <select
            value={form.enabled ? "true" : "false"}
            onChange={e => set("enabled", e.target.value === "true")}
            className={inputClass()}
          >
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
        )}
      </div>
      {touched && errors.route && target.seeded && (
        <p className="mt-3 text-[11.5px] text-red-600">{errors.route}</p>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
        <Button variant="primary" onClick={save}>
          {needsReview || isNew ? "Save as confirmed target" : "Save target"}
        </Button>
        <Button variant="white" onClick={onClose}>
          Cancel
        </Button>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={target.mappings.length > 0}
            title={
              target.mappings.length > 0
                ? "Remap the CTAs that use this target first."
                : "Remove this target"
            }
            className="ml-auto text-[12px] font-semibold text-red-700 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
          >
            Remove target
          </button>
        )}
      </div>
      {onRemove && target.mappings.length > 0 && (
        <p className="mt-2 text-[11.5px] text-gray-500">
          {target.mappings.length} CTA mapping
          {target.mappings.length === 1 ? "" : "s"} still use this target —
          remap them before removing it.
        </p>
      )}
    </Drawer>
  );
};

const TargetRegistryPanel: React.FC<TargetRegistryPanelProps> = ({
  registry,
  draft,
  appliedTargets,
  onDraftChange,
  onOpenMappings,
}) => {
  const [panel, setPanel] = useState<Panel | null>(null);
  const [fallback, setFallback] = useState("");

  const targets = useMemo(
    () =>
      draftTargets(registry, draft).sort(
        (a, b) =>
          REVIEW_ORDER[a.review_status] - REVIEW_ORDER[b.review_status] ||
          a.label.localeCompare(b.label)
      ),
    [registry, draft]
  );
  const byId = new Map(targets.map(t => [t.id, t]));
  const titleOf = (id: string) =>
    registry.definitions.find(d => d.id === id)?.title ?? id;
  const close = () => setPanel(null);

  const openAvailability = (id: string, mode: "disable" | "reject") => {
    setFallback(byId.get(id)?.fallback_target ?? "");
    setPanel({ kind: "availability", id, mode });
  };

  const stage = (id: string, patch: TargetPatch) =>
    onDraftChange(stageTarget(draft, id, patch));

  const inspected = panel?.kind === "inspect" ? byId.get(panel.id) : null;
  const editing =
    panel?.kind === "edit"
      ? panel.id
        ? byId.get(panel.id)
        : newTargetRecord("")
      : null;
  const changing = panel?.kind === "availability" ? byId.get(panel.id) : null;
  const fallbackOptions = changing
    ? targets.filter(t => t.id !== changing.id && t.accepts_new_mappings)
    : [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
            Delivery routing
          </span>
          <h3 className="text-[15px] font-bold text-gray-900">
            Target Registry
          </h3>
          <p className="mt-0.5 max-w-3xl text-[12.5px] text-gray-500">
            The ExecBridge pages and tabs a signal card&apos;s action can send
            users to. Review imported legacy references, add real targets, and
            decide what happens to a card when its target is unavailable.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setPanel({ kind: "edit", id: null })}
        >
          Add target
        </Button>
      </div>

      <div className="rounded-lg border border-brand/20 bg-brand-faint px-4 py-2.5 text-[12.5px] text-gray-700">
        <strong className="text-gray-900">Legacy migration</strong> Imported
        references keep routing their existing CTAs while they&apos;re marked
        Needs review. Only confirmed targets can receive new CTA mappings. A
        disabled or rejected target sends its cards to the fallback you pick, or
        stops delivering them.
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full min-w-[980px] border-collapse text-left">
          <thead>
            <tr className="bg-gray-50 text-[10px] font-bold uppercase tracking-[0.07em] text-gray-400">
              <th className="px-4 py-2">Target</th>
              <th className="px-3 py-2">Type / parent</th>
              <th className="px-3 py-2">Route</th>
              <th className="px-3 py-2">Mapped CTAs</th>
              <th className="px-3 py-2">Review status</th>
              <th className="px-3 py-2">Organization state</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-[12.5px]">
            {targets.map(t => {
              const staged =
                JSON.stringify(draft.targets[t.id] ?? null) !==
                JSON.stringify(appliedTargets[t.id] ?? null);
              const review = REVIEW_META[t.review_status];
              return (
                <tr key={t.id} data-target-id={t.id} className="align-top">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">
                        {t.label}
                      </span>
                      {staged && (
                        <span className="text-[10.5px] font-semibold text-amber-700">
                          Unapplied
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10.5px] text-gray-400">
                      {t.id}
                    </div>
                    <div className="text-[10.5px] text-gray-400">
                      {t.provenance}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-700">
                    {SURFACE_LABEL[t.surface_type]}
                    {t.parent_page && (
                      <div className="text-[11px] text-gray-400">
                        {t.parent_page}
                      </div>
                    )}
                  </td>
                  <td className="max-w-[240px] break-all px-3 py-2.5 font-mono text-[11.5px] text-gray-600">
                    {routeText(t)}
                  </td>
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() => onOpenMappings(t.id)}
                      className="whitespace-nowrap rounded-md border border-gray-200 px-2.5 py-1 text-[12px] font-semibold text-gray-700 hover:border-brand hover:text-brand"
                    >
                      {t.mappings.length} CTA mapping
                      {t.mappings.length === 1 ? "" : "s"} →
                    </button>
                  </td>
                  <td className="px-3 py-2.5">
                    <Pill className={review.className}>{review.label}</Pill>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <Pill
                        className={
                          t.available
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                            : "bg-red-50 text-red-700 ring-red-600/20"
                        }
                      >
                        {t.available ? "Enabled" : "Disabled"}
                      </Pill>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={t.available}
                        aria-label={`${t.available ? "Disable" : "Enable"} ${t.label}`}
                        disabled={t.review_status === "rejected"}
                        title={
                          t.review_status === "rejected"
                            ? "Review and confirm a rejected reference before enabling it."
                            : undefined
                        }
                        onClick={() =>
                          t.available
                            ? openAvailability(t.id, "disable")
                            : stage(t.id, { enabled: true })
                        }
                        className={`relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          t.available ? "bg-emerald-600" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                            t.available ? "left-[18px]" : "left-0.5"
                          }`}
                        />
                      </button>
                    </div>
                    {!t.available && t.fallback_target && (
                      <div className="mt-1 text-[11px] text-gray-500">
                        Falls back to{" "}
                        {byId.get(t.fallback_target)?.label ??
                          t.fallback_target}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => setPanel({ kind: "inspect", id: t.id })}
                        className="rounded-md border border-gray-200 px-2.5 py-1 text-[12px] font-semibold text-gray-600 hover:border-gray-300 hover:text-gray-900"
                      >
                        Inspect
                      </button>
                      <button
                        type="button"
                        onClick={() => setPanel({ kind: "edit", id: t.id })}
                        className="rounded-md border border-gray-200 px-2.5 py-1 text-[12px] font-semibold text-gray-600 hover:border-gray-300 hover:text-gray-900"
                      >
                        {t.review_status === "needs_review" ? "Review" : "Edit"}
                      </button>
                      {t.review_status === "needs_review" && (
                        <button
                          type="button"
                          onClick={() => openAvailability(t.id, "reject")}
                          className="rounded-md border border-red-200 px-2.5 py-1 text-[12px] font-semibold text-red-700 hover:border-red-300 hover:bg-red-50"
                        >
                          Reject
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {inspected && (
        <Drawer
          open
          onClose={close}
          eyebrow={
            <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
              ExecBridge target
            </span>
          }
          title={inspected.label}
          subtitle={inspected.id}
        >
          <dl className="m-0 mb-6 text-[12.5px]">
            {(
              [
                ["Description", inspected.description || "—"],
                [
                  "Type",
                  `${SURFACE_LABEL[inspected.surface_type]}${inspected.parent_page ? ` · ${inspected.parent_page}` : ""}`,
                ],
                ["Review status", REVIEW_META[inspected.review_status].label],
                ["Provenance", inspected.provenance],
                [
                  "Organization state",
                  inspected.available ? "Enabled" : "Disabled",
                ],
                ["Route", routeText(inspected)],
                ...(inspected.legacy_route
                  ? [["Legacy reference", inspected.legacy_route]]
                  : []),
                ["Required context", inspected.required_context || "—"],
                ["Personas", inspected.personas.join(" · ") || "None"],
                ["Permission", inspected.permission || "—"],
                [
                  "When unavailable",
                  inspected.fallback_target
                    ? `Route to ${byId.get(inspected.fallback_target)?.label ?? inspected.fallback_target}`
                    : "Stop delivering its cards",
                ],
              ] as [string, string][]
            ).map(([label, value]) => (
              <div
                key={label}
                className="grid grid-cols-[140px_minmax(0,1fr)] gap-3 py-1.5"
              >
                <dt className="text-gray-500">{label}</dt>
                <dd className="m-0 break-words text-gray-900">{value}</dd>
              </div>
            ))}
          </dl>
          <h3 className="mb-2.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-gray-400">
            Mapped CTA configurations · {inspected.mappings.length}
          </h3>
          {inspected.mappings.length ? (
            <div className="flex flex-wrap gap-1.5">
              {inspected.mappings.map(id => (
                <span
                  key={id}
                  className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11.5px] text-gray-700"
                >
                  {titleOf(id)}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-[12px] text-gray-500">
              No CTA configurations map to this target.
            </p>
          )}
          <p className="mt-6 text-[11.5px] leading-relaxed text-gray-500">
            Target changes affect cards from the next time they load. Action
            History keeps the CTA each user actually saw.
          </p>
          <div className="mt-4">
            <Button
              variant="primary"
              onClick={() => setPanel({ kind: "edit", id: inspected.id })}
            >
              {inspected.review_status === "needs_review"
                ? "Review and classify"
                : "Edit target"}
            </Button>
          </div>
        </Drawer>
      )}

      {editing && panel?.kind === "edit" && (
        <TargetEditor
          key={panel.id ?? "new"}
          target={editing}
          isNew={panel.id === null}
          existingIds={new Set(targets.map(t => t.id))}
          onSave={(id, patch) => {
            stage(id, patch);
            close();
          }}
          onClose={close}
          onRemove={
            panel.id && !editing.seeded
              ? () => {
                  onDraftChange(
                    stageTargetRemoval(
                      draft,
                      editing.id,
                      editing.id in appliedTargets
                    )
                  );
                  close();
                }
              : undefined
          }
        />
      )}

      {changing && panel?.kind === "availability" && (
        <Drawer
          open
          onClose={close}
          eyebrow={
            <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
              {panel.mode === "reject"
                ? "Reject legacy reference"
                : "Disable target"}
            </span>
          }
          title={changing.label}
          subtitle={changing.id}
        >
          <p className="mb-4 text-[12.5px] text-gray-600">
            {panel.mode === "reject"
              ? "Rejecting records that this isn't a real ExecBridge page or tab. It's taken out of routing for good."
              : "While disabled, cards whose action points here use the fallback below."}
          </p>
          <label className="flex flex-col gap-1">
            <span className={labelClass}>When this target is unavailable</span>
            <select
              value={fallback}
              onChange={e => setFallback(e.target.value)}
              className={inputClass()}
            >
              <option value="">Stop delivering these cards</option>
              {fallbackOptions.map(t => (
                <option key={t.id} value={t.id}>
                  Route to {t.label}
                </option>
              ))}
            </select>
            {fallbackOptions.length === 0 && (
              <span className="text-[11.5px] text-gray-500">
                Only confirmed, enabled targets can be a fallback — confirm one
                first to reroute instead.
              </span>
            )}
          </label>
          <ul className="mt-4 list-disc pl-5 text-[12px] text-gray-600">
            <li>
              {changing.mappings.length} CTA mapping
              {changing.mappings.length === 1 ? "" : "s"} use this target
            </li>
            <li>
              {fallback
                ? `Those cards will open ${byId.get(fallback)?.label}`
                : "Those cards stop being shown; the signals are still evaluated and recorded"}
            </li>
            <li>Nothing changes for users until you apply the draft</li>
          </ul>
          <div className="mt-6 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
            <Button
              variant="primary"
              onClick={() => {
                stage(changing.id, {
                  ...(panel.mode === "reject"
                    ? { review_status: "rejected" }
                    : { enabled: false }),
                  fallback_target: fallback,
                });
                close();
              }}
            >
              {panel.mode === "reject" ? "Stage rejection" : "Stage disable"}
            </Button>
            <Button variant="white" onClick={close}>
              Cancel
            </Button>
          </div>
        </Drawer>
      )}
    </div>
  );
};

export default TargetRegistryPanel;
